// Date helpers that work on plain YYYY-MM-DD strings in the browser's local
// time zone, so a stored date never shifts by a day across time zones.

const DAY_MS = 86400000

const pad = (value: number): string => String(value).padStart(2, '0')

export const toISODate = (date: Date): string => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const parseISODate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(year || 1970, (month || 1) - 1, day || 1)
}

export const isISODate = (value: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
    return toISODate(parseISODate(value)) === value
}

export const todayISO = (): string => toISODate(new Date())

export const addDays = (value: string, days: number): string => {
    const date = parseISODate(value)
    date.setDate(date.getDate() + days)
    return toISODate(date)
}

export const daysInMonth = (year: number, monthIndex: number): number => {
    return new Date(year, monthIndex + 1, 0).getDate()
}

export const addMonths = (value: string, months: number): string => {
    const date = parseISODate(value)
    const day = date.getDate()
    date.setDate(1)
    date.setMonth(date.getMonth() + months)
    date.setDate(Math.min(day, daysInMonth(date.getFullYear(), date.getMonth())))
    return toISODate(date)
}

export const daysBetween = (from: string, to: string): number => {
    return Math.round((parseISODate(to).getTime() - parseISODate(from).getTime()) / DAY_MS)
}

export const startOfWeek = (value: string, weekStartsOn = 1): string => {
    const date = parseISODate(value)
    const diff = (date.getDay() - weekStartsOn + 7) % 7
    date.setDate(date.getDate() - diff)
    return toISODate(date)
}

export const startOfMonth = (value: string): string => `${value.slice(0, 7)}-01`

export const endOfMonth = (value: string): string => {
    const date = parseISODate(value)
    return toISODate(new Date(date.getFullYear(), date.getMonth() + 1, 0))
}

export const monthKey = (value: string): string => value.slice(0, 7)

export const isSameMonth = (a: string, b: string): boolean => monthKey(a) === monthKey(b)

export const dayOfWeek = (value: string): number => parseISODate(value).getDay()

export const eachDay = (from: string, to: string): string[] => {
    const days: string[] = []
    if (!from || !to || from > to) return days
    let current = from
    while (current <= to && days.length < 1500) {
        days.push(current)
        current = addDays(current, 1)
    }
    return days
}

export const isWithin = (value: string, from: string, to: string): boolean => {
    return value >= from && value <= to
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
const shortDateFormatter = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' })
const longDateFormatter = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const weekdayDateFormatter = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
const monthYearFormatter = new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' })
const shortMonthFormatter = new Intl.DateTimeFormat('en-IN', { month: 'short' })
const shortMonthYearFormatter = new Intl.DateTimeFormat('en-IN', { month: 'short', year: '2-digit' })
const weekdayFormatter = new Intl.DateTimeFormat('en-IN', { weekday: 'short' })
const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

export const formatDate = (value: string): string => (value ? dateFormatter.format(parseISODate(value)) : '')

export const formatShortDate = (value: string): string => (value ? shortDateFormatter.format(parseISODate(value)) : '')

export const formatLongDate = (value: string): string => (value ? longDateFormatter.format(parseISODate(value)) : '')

export const formatWeekdayDate = (value: string): string => (value ? weekdayDateFormatter.format(parseISODate(value)) : '')

export const formatMonthYear = (value: string): string => (value ? monthYearFormatter.format(parseISODate(value)) : '')

export const formatShortMonth = (value: string): string => (value ? shortMonthFormatter.format(parseISODate(value)) : '')

export const formatShortMonthYear = (value: string): string => (value ? shortMonthYearFormatter.format(parseISODate(value)) : '')

export const formatWeekday = (value: string): string => (value ? weekdayFormatter.format(parseISODate(value)) : '')

export const formatDateTime = (value: string): string => {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '' : dateTimeFormatter.format(date)
}

export const timeToMinutes = (value: string): number => {
    const [hours, minutes] = value.split(':').map(Number)
    return (hours || 0) * 60 + (minutes || 0)
}

export const minutesToTime = (total: number): string => {
    const safe = Math.max(0, Math.min(total, 24 * 60 - 1))
    return `${pad(Math.floor(safe / 60))}:${pad(safe % 60)}`
}

export const formatTime = (value: string): string => {
    if (!value) return ''
    const [hours, minutes] = value.split(':').map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const hour = hours % 12 || 12
    return `${hour}:${pad(minutes || 0)} ${period}`
}

export const relativeDayLabel = (value: string, today: string): string => {
    if (!value || !today) return ''
    const diff = daysBetween(today, value)
    if (diff === 0) return 'Today'
    if (diff === 1) return 'Tomorrow'
    if (diff === -1) return 'Yesterday'
    if (diff > 1) return `In ${diff} days`
    return `${Math.abs(diff)} days ago`
}

export const ageFromDob = (dob: string, today: string): number | null => {
    if (!dob || !today) return null
    const birth = parseISODate(dob)
    const now = parseISODate(today)
    let age = now.getFullYear() - birth.getFullYear()
    const beforeBirthday = now.getMonth() < birth.getMonth()
        || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
    if (beforeBirthday) age -= 1
    return age >= 0 ? age : null
}

// Stamp a local calendar date and clock time as a full ISO timestamp.
export const stampAt = (date: string, time = '10:00'): string => {
    const base = parseISODate(date)
    const [hours, minutes] = time.split(':').map(Number)
    base.setHours(hours || 0, minutes || 0, 0, 0)
    return base.toISOString()
}

export const isoDateFromStamp = (value: string): string => {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '' : toISODate(date)
}
